import React, { FC, useState } from 'react';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { T } from 'src/shared/helpers';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { getToolLabel, ProductSalesChannelInfo } from '../common/constants';
import {
    ChannelDisconnectButton,
    ChannelDot,
    ChannelItem,
    ChannelMeta,
    ChannelsBadge,
    ChannelsDropdown,
    EmptyState,
    GetAQuoteChip,
    InlineAction,
    ItemDot,
    ItemName,
    ItemNameAction,
    LearnMoreLink,
    PanelItemSpaced,
    StatusBadge,
    StatusBar,
    StatusBarSpacer,
    ToolBadge
} from '../common/styled';

// Preserves the sidebar's Get a quote information display inside a popover opened from the
// status-bar chip, instead of a full category. Three states:
//  - a rule is active on the product      → the rule(s) + Edit/Unassign + Go to Tool Settings
//  - no active rule but rules in account   → assignable rules list (Assign) + Add rule
//  - no rules in the account               → empty state with guide link + Set up
export interface GetAQuoteChipData {
    active: boolean;
    items: { id: string; name: string }[];
    assignableRules: { id: string; name: string }[];
    description: string;
    helpUrl?: string;
    onEdit: (id: string) => void;
    onUnassign: (id: string, name: string) => void;
    onGoToSettings: () => void;
    onAssign: (id: string) => void;
    onCreateNew: () => void;
}

export interface StatusBarSectionProps {
    zakekeEnvironment: string;
    isOnline: boolean;
    salesChannelCount: number;
    salesChannels: ProductSalesChannelInfo[];
    channelsOpen: boolean;
    onToggleChannels: () => void;
    onSalesChannelAction?: () => void;
    salesChannelActionLabel?: React.ReactNode;
    onDisconnectSalesChannel?: (channel: ProductSalesChannelInfo) => void;
    getAQuote?: GetAQuoteChipData;
}

export const StatusBarSection: FC<StatusBarSectionProps> = ({
    zakekeEnvironment,
    isOnline,
    salesChannelCount,
    salesChannels,
    channelsOpen,
    onToggleChannels,
    onSalesChannelAction,
    salesChannelActionLabel,
    onDisconnectSalesChannel,
    getAQuote
}) => {
    const [quoteOpen, setQuoteOpen] = useState(false);

    const runQuoteAction = (action: () => void) => {
        setQuoteOpen(false);
        action();
    };

    return (
        <>
            {/* Status bar: tool + status + sales channels + get a quote */}
            <StatusBar>
                <ToolBadge $type={zakekeEnvironment}>{getToolLabel(zakekeEnvironment)}</ToolBadge>
                <StatusBadge $online={isOnline}>{isOnline ? T._('Online', 'Admin') : T._('Not published', 'Admin')}</StatusBadge>
                <StatusBarSpacer />
                <ChannelsBadge $count={salesChannelCount} onClick={onToggleChannels}>
                    {salesChannelCount > 0
                        ? salesChannelCount + ' ' + (salesChannelCount === 1 ? T._('Sales Channel', 'Admin') : T._('Sales Channels', 'Admin'))
                        : T._('No Sales Channels', 'Admin')}
                    {salesChannelCount > 0 && <span>{channelsOpen ? '▴' : '▾'}</span>}
                </ChannelsBadge>
                {getAQuote && (
                    <ZTooltip position='top' title={getAQuote.active ? T._('Get a quote is active', 'Admin') : T._('Set up Get a quote', 'Admin')}>
                        <GetAQuoteChip type='button' $active={getAQuote.active} onClick={() => setQuoteOpen((open) => !open)}>
                            {T._('Get a quote', 'Admin')}
                            <span>{quoteOpen ? '▴' : '▾'}</span>
                        </GetAQuoteChip>
                    </ZTooltip>
                )}
            </StatusBar>

            {/* Get a quote popover: active rule / assignable rules / empty state */}
            {getAQuote && quoteOpen && (
                <ChannelsDropdown>
                    {getAQuote.active ? (
                        <>
                            {getAQuote.items.map((rule) => (
                                <ChannelItem key={rule.id}>
                                    <ChannelDot />
                                    <ItemName>{rule.name}</ItemName>
                                    <InlineAction $danger onClick={() => runQuoteAction(() => getAQuote.onUnassign(rule.id, rule.name))}>
                                        {T._('Unassign', 'Admin')}
                                    </InlineAction>
                                    <InlineAction onClick={() => runQuoteAction(() => getAQuote.onEdit(rule.id))}>{T._('Edit', 'Admin')} &#8250;</InlineAction>
                                </ChannelItem>
                            ))}
                            <PanelItemSpaced $clickable onClick={() => runQuoteAction(getAQuote.onGoToSettings)}>
                                <ItemNameAction>{T._('Go to Tool Settings', 'Admin')} &#8250;</ItemNameAction>
                            </PanelItemSpaced>
                        </>
                    ) : getAQuote.assignableRules.length > 0 ? (
                        <>
                            {getAQuote.assignableRules.map((rule) => (
                                <ChannelItem key={rule.id}>
                                    <ChannelDot />
                                    <ItemName>{rule.name}</ItemName>
                                    <InlineAction onClick={() => getAQuote.onAssign(rule.id)}>{T._('Assign', 'Admin')}</InlineAction>
                                </ChannelItem>
                            ))}
                            <PanelItemSpaced $clickable onClick={() => runQuoteAction(getAQuote.onCreateNew)}>
                                <ItemDot $color='#d1d5db' />
                                <ItemNameAction>
                                    <Icon>
                                        <PlusIcon />
                                    </Icon>
                                    {T._('Add rule', 'Admin')}
                                </ItemNameAction>
                            </PanelItemSpaced>
                        </>
                    ) : (
                        <>
                            <EmptyState>
                                {getAQuote.description}
                                {getAQuote.helpUrl && (
                                    <>
                                        {' — '}
                                        <LearnMoreLink href={getAQuote.helpUrl} target='_blank' rel='noopener'>
                                            {T._('Learn more', 'Admin')} &#8250;
                                        </LearnMoreLink>
                                    </>
                                )}
                            </EmptyState>
                            <PanelItemSpaced $clickable onClick={() => runQuoteAction(getAQuote.onCreateNew)}>
                                <ItemDot $color='#d1d5db' />
                                <ItemNameAction>
                                    <Icon>
                                        <PlusIcon />
                                    </Icon>
                                    {T._('Set up', 'Admin')}
                                </ItemNameAction>
                            </PanelItemSpaced>
                        </>
                    )}
                </ChannelsDropdown>
            )}

            {/* Channels dropdown */}
            {channelsOpen && (
                <ChannelsDropdown>
                    {salesChannels.map((salesChannel, channelIndex) => (
                        <ChannelItem key={channelIndex}>
                            <ChannelDot />
                            {salesChannel.url ? (
                                <ZTooltip position='top' title={salesChannel.url}>
                                    <span>{salesChannel.name}</span>
                                </ZTooltip>
                            ) : (
                                <span>{salesChannel.name}</span>
                            )}
                            <ChannelMeta>{salesChannel.ecommerce}</ChannelMeta>
                            {salesChannel.isOtherSalesChannel && onDisconnectSalesChannel && (
                                <ChannelDisconnectButton onClick={() => onDisconnectSalesChannel(salesChannel)}>
                                    {T._('Disconnect', 'Admin')}
                                </ChannelDisconnectButton>
                            )}
                        </ChannelItem>
                    ))}
                    {onSalesChannelAction && (
                        <PanelItemSpaced $clickable onClick={() => onSalesChannelAction()}>
                            <ItemDot $color='#d1d5db' />
                            <ItemNameAction>
                                {salesChannelActionLabel ?? (
                                    <>
                                        <Icon>
                                            <PlusIcon />
                                        </Icon>
                                        {T._('Add to a Sales Channel', 'Admin')}
                                    </>
                                )}
                            </ItemNameAction>
                        </PanelItemSpaced>
                    )}
                </ChannelsDropdown>
            )}
        </>
    );
};
